/**
 * ReportsTab — rapports comptables : grand livre (balance par période, transactions GL), états financiers (résultats, bilan, complets),
 * fournisseurs (journal du mois, âge des comptes), clients (journal du mois, âge des comptes). Écran + PDF.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { FileDown, Loader2, Target } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, downloadPdf, errMsg, today, useGlBase } from "./_shared";
import { RANGE_PRESETS, presetRange } from "@/components/ListTools";
import TrialBalanceReport from "./reports/TrialBalanceReport";
import GlTransactionsReport from "./reports/GlTransactionsReport";
import { BalanceSheetReport, FinancialStatementsReport, IncomeStatementReport } from "./reports/StatementsReport";
import PartyJournalReport from "./reports/PartyJournalReport";
import AgingReport from "./reports/AgingReport";
import StatementActions from "./reports/StatementActions";
import BudgetDialog from "./reports/BudgetDialog";
import CashForecastReport from "./reports/CashForecastReport";

const GROUPS = [
  { label: "Grand livre", items: [
    { id: "trial-balance", label: "Balance de vérification", params: "period", query: (p) => ({ date_from: p.from || undefined, as_of: p.to }), file: (p) => `Balance_verification_${p.to}.pdf`, C: TrialBalanceReport, optionalFrom: true },
    { id: "gl-transactions", label: "Transactions GL", params: "period", query: (p) => ({ date_from: p.from, date_to: p.to }), file: (p) => `Transactions_GL_${p.from}_${p.to}.pdf`, C: GlTransactionsReport },
  ] },
  { label: "États financiers", items: [
    { id: "income-statement", label: "État des résultats", params: "period", query: (p) => ({ date_from: p.from, date_to: p.to }), file: (p) => `Etat_resultats_${p.from}_${p.to}.pdf`, C: IncomeStatementReport },
    { id: "balance-sheet", label: "Bilan", params: "as_of", query: (p) => ({ as_of: p.to }), file: (p) => `Bilan_${p.to}.pdf`, C: BalanceSheetReport },
    { id: "financial-statements", label: "États financiers complets", params: "as_of", query: (p) => ({ as_of: p.to }), file: (p) => `Etats_financiers_${p.to}.pdf`, C: FinancialStatementsReport },
  ] },
  { label: "Trésorerie", items: [
    { id: "cash-forecast", label: "Prévision 30 · 60 · 90 jours", params: "as_of", query: (p) => ({ as_of: p.to, include_budget: p.budget ? 1 : undefined }), file: () => "", C: CashForecastReport, noPdf: true },
  ] },
  { label: "Fournisseurs", items: [
    { id: "supplier-journal", label: "Journal du mois", params: "month", party: "supplier", query: (p) => ({ month: p.month, party: p.party || undefined }), file: (p) => `Journal_fournisseurs_${p.month}.pdf`, C: PartyJournalReport },
    { id: "supplier-aging", label: "Âge des comptes", params: "as_of", party: "supplier", query: (p) => ({ as_of: p.to, party: p.party || undefined }), file: (p) => `Age_comptes_fournisseurs_${p.to}.pdf`, C: AgingReport },
  ] },
  { label: "Clients", items: [
    { id: "customer-journal", label: "Journal du mois", params: "month", party: "customer", query: (p) => ({ month: p.month, party: p.party || undefined }), file: (p) => `Journal_clients_${p.month}.pdf`, C: PartyJournalReport },
    { id: "customer-aging", label: "Âge des comptes", params: "as_of", party: "customer", query: (p) => ({ as_of: p.to, party: p.party || undefined }), file: (p) => `Age_comptes_clients_${p.to}.pdf`, C: AgingReport },
  ] },
];
const ALL = GROUPS.flatMap((g) => g.items);
const dateCls = "border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white";

/** Sélecteur « tous / un seul » fournisseur ou client (dossier maître avec n° au cabinet · fiche en tenue de livres). */
function PartySelect({ kind, value, onChange }) {
  const base = useGlBase();
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    let alive = true;
    const url = kind === "supplier" ? `${base}/suppliers` : `${base}/customers`;
    api.get(url, { params: kind === "supplier" ? { include_inactive: true } : {} }).then((r) => {
      if (!alive) return;
      setOpts((r.data.items || []).map((x) => ({ id: x.id, label: x.label || x.name })));
    }).catch(() => setOpts([]));
    return () => { alive = false; };
  }, [kind, base]);
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${dateCls} max-w-xs`} data-testid="report-party-select">
      <option value="">{kind === "supplier" ? "Tous les fournisseurs" : "Tous les clients"}</option>
      {opts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  );
}

export default function ReportsTab({ settings, only }) {
  const base = useGlBase();
  const [sp, setSp] = useSearchParams();
  const groups = only ? GROUPS.map((g) => ({ ...g, items: g.items.filter((r) => only.includes(r.id)) })).filter((g) => g.items.length) : GROUPS;
  const all = groups.flatMap((g) => g.items);
  const kind = all.some((r) => r.id === sp.get("report")) ? sp.get("report") : all[0].id;
  const def = all.find((r) => r.id === kind);
  const fyStart = `${new Date().getFullYear()}-${String(settings.fiscal_year_start_month || 1).padStart(2, "0")}-01`;
  const [p, setP] = useState({ from: fyStart, to: today(), month: today().slice(0, 7), party: sp.get("party") || "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const setKind = (id) => { const n = new URLSearchParams(sp); n.set("report", id); n.delete("party"); setSp(n, { replace: true }); setP((x) => ({ ...x, party: "" })); };
  const setParty = (v) => { const n = new URLSearchParams(sp); if (v) n.set("party", v); else n.delete("party"); setSp(n, { replace: true }); setP((x) => ({ ...x, party: v })); };
  const ready = def.params === "month" ? !!p.month : def.params === "as_of" ? !!p.to : !!p.to && (def.optionalFrom || !!p.from);

  const reqRef = useRef(0);
  const load = useCallback(async () => {
    if (!ready) return;
    const id = ++reqRef.current;
    setData(null); setLoading(true);
    try { const r = await api.get(`${base}/reports/${kind}`, { params: def.query(p) }); if (id === reqRef.current) setData({ ...r.data, _kind: kind }); }
    catch (e) { if (id === reqRef.current) toast.error(errMsg(e, "Rapport indisponible")); }
    finally { if (id === reqRef.current) setLoading(false); }
  }, [kind, def, p, ready, base]);
  useEffect(() => { load(); }, [load]);

  const pdf = () => {
    const q = Object.fromEntries(Object.entries(def.query(p)).filter(([, v]) => v));
    downloadPdf(`${base}/reports/${kind}/pdf?${new URLSearchParams(q).toString()}`, def.file(p), setBusy);
  };
  const Report = def.C;

  return (
    <div className="space-y-4" data-testid="reports-tab">
      <div className="flex flex-wrap gap-x-6 gap-y-2" data-testid="report-groups">
        {groups.map((g) => (
          <div key={g.label} className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{g.label}</span>
            <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
              {g.items.map((r) => (
                <button key={r.id} onClick={() => setKind(r.id)} className={`px-3 py-1.5 text-xs font-semibold transition ${kind === r.id ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid={`report-kind-${r.id}`}>{r.label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2 bg-white border rounded-xl px-3 py-2">
        <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
          <span className="font-bold text-slate-800 text-sm mr-2">{def.label}</span>
          {def.params === "month" && (<>Mois <input type="month" value={p.month} onChange={(e) => setP({ ...p, month: e.target.value })} className={dateCls} data-testid="report-month" /></>)}
          {def.params === "period" && (<>
            Du <input type="date" value={p.from} onChange={(e) => setP({ ...p, from: e.target.value })} className={dateCls} data-testid="report-date-from" />
            {def.optionalFrom && p.from && <button onClick={() => setP({ ...p, from: "" })} className="underline" data-testid="report-clear-from" title="Balance cumulative depuis l'origine">cumulatif</button>}
            au <input type="date" value={p.to} onChange={(e) => setP({ ...p, to: e.target.value })} className={dateCls} data-testid="report-as-of" />
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" role="group" aria-label="Période rapide" data-testid="report-presets">
              {RANGE_PRESETS.filter(([id]) => id !== "all").map(([id, label]) => {
                const r0 = presetRange(id, settings.fiscal_year_start_month || 1);
                const r = { from: r0.from, to: (id === "year" || id === "quarter") && r0.to > today() ? today() : r0.to };
                const on = p.from === r.from && p.to === r.to;
                return <button key={id} type="button" onClick={() => setP({ ...p, from: r.from, to: r.to })} className={`px-2 py-1 text-[11px] rounded-md ${on ? "bg-slate-800 text-white font-semibold" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`report-preset-${id}`}>{label}</button>;
              })}
            </div>
          </>)}
          {def.params === "as_of" && (<>Au <input type="date" value={p.to} onChange={(e) => setP({ ...p, to: e.target.value })} className={dateCls} data-testid="report-as-of" /></>)}
          {def.party && <PartySelect kind={def.party} value={p.party} onChange={setParty} />}
          {kind === "cash-forecast" && <label className="inline-flex items-center gap-1.5 ml-2 cursor-pointer"><input type="checkbox" checked={!!p.budget} onChange={(e) => setP({ ...p, budget: e.target.checked })} data-testid="cash-include-budget" /> inclure le budget mensuel</label>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {kind === "income-statement" && <Btn tone="slate" icon={Target} onClick={() => setBudgetOpen(true)} testId="report-budget-btn" title="Budget annuel par compte — colonnes Budget et Écart">{data?.budget ? "Budget ✓" : "Budget"}</Btn>}
          <StatementActions kind={kind} query={def.query(p)} ready={!!data && data._kind === kind} />
          {!def.noPdf && <Btn tone="slate" icon={FileDown} busy={busy} disabled={!data} onClick={pdf} testId="report-pdf-btn">PDF</Btn>}
        </div>
      </div>

      {budgetOpen && <BudgetDialog dateFrom={p.from} onClose={() => setBudgetOpen(false)} onSaved={load} />}
      {kind === "cash-forecast" && <p className="text-[11px] text-slate-500">Encaisse des comptes bancaires cochés + factures clients à recevoir (par échéance) − achats à payer − remise TPS/TVQ en cours ; le budget mensuel ajoute les revenus / dépenses prévus non encore facturés. Le budget se saisit dans l'état des résultats (bouton Budget).</p>}

      {loading && <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}
      {data?._kind === kind && <Report data={data} />}
    </div>
  );
}
